import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { EarlyAccessFilters } from '../types/early-access';

const STORAGE_KEY = 'sparkadmin_early_access_filters';

const DEFAULT_FILTERS: EarlyAccessFilters = {
  search: '',
  page: 1,
  pageSize: 25,
  sortBy: 'createdAt',
  sortOrder: 'desc',
};

function parseFiltersFromURL(searchParams: URLSearchParams): Partial<EarlyAccessFilters> {
  const filters: Partial<EarlyAccessFilters> = {};

  const search = searchParams.get('search');
  if (search) filters.search = search;

  const page = searchParams.get('page');
  if (page) filters.page = parseInt(page, 10);

  const pageSize = searchParams.get('pageSize');
  if (pageSize) filters.pageSize = parseInt(pageSize, 10);

  const sortOrder = searchParams.get('sortOrder');
  if (sortOrder && ['asc', 'desc'].includes(sortOrder)) {
    filters.sortOrder = sortOrder as EarlyAccessFilters['sortOrder'];
  }

  return filters;
}

function filtersToSearchParams(filters: EarlyAccessFilters): URLSearchParams {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.page !== 1) params.set('page', filters.page.toString());
  if (filters.pageSize !== 25) params.set('pageSize', filters.pageSize.toString());
  if (filters.sortOrder !== 'desc') params.set('sortOrder', filters.sortOrder);

  return params;
}

function loadFiltersFromStorage(): Partial<EarlyAccessFilters> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveFiltersToStorage(filters: EarlyAccessFilters): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

export function useEarlyAccessFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<EarlyAccessFilters>(() => {
    const urlFilters = parseFiltersFromURL(searchParams);
    const storageFilters = loadFiltersFromStorage();
    return {
      ...DEFAULT_FILTERS,
      ...storageFilters,
      ...urlFilters,
    };
  });

  useEffect(() => {
    const params = filtersToSearchParams(filters);
    setSearchParams(params, { replace: true });
    saveFiltersToStorage(filters);
  }, [filters, setSearchParams]);

  const updateFilter = useCallback(
    (key: keyof EarlyAccessFilters, value: EarlyAccessFilters[keyof EarlyAccessFilters]) => {
      setFilters((prev) => {
        const updated = { ...prev, [key]: value };
        if (key !== 'page') {
          updated.page = 1;
        }
        return updated;
      });
    },
    []
  );

  const clearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  return {
    filters,
    updateFilter,
    clearFilters,
  };
}
